import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Share2, TrendingUp, AlertTriangle, DollarSign, Twitter } from 'lucide-react';
import { ACCOUNT_TYPES, getAccountType, ACCOUNT_TYPE_TONE } from '@/lib/accountTypes';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTOR_COLORS = [
  '#8b5cf6', '#10b981', '#3b82f6', '#f59e0b', '#ef4444',
  '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#64748b',
  '#a855f7', '#14b8a6',
];

export default function SharedPortfolio() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  // P108 (Feb 2026) — Manual-refresh state. Click the "Refreshed X ago"
  // pill → POST /share/{slug}/refresh → replace state with fresh snap.
  // Backend throttles to 1 rebuild per 30s (returns the same snapshot
  // silently if hammered), so we don't need our own client-side lock
  // beyond a spinner during the in-flight request.
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/share/${slug}`)
      .then(res => setData(res.data))
      .catch(err => {
        if (err?.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const res = await axios.post(`${API}/share/${slug}/refresh`);
      setData(res.data);
    } catch {
      // Silent — pill just stays on the old timestamp. Not worth an
      // alert modal for a background refresh.
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
          <h2 className="text-xl font-bold mb-2">Share link not found</h2>
          <p className="text-sm text-muted-foreground mb-4">
            This portfolio share link may have been regenerated or deleted by its owner.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold bg-violet-600 text-white hover:bg-violet-700"
            data-testid="share-not-found-home-btn"
          >
            Go to DiviDrip
          </Link>
        </div>
      </div>
    );
  }

  const showDollars = !!data.show_dollar_amounts;
  const created = data.created_at ? new Date(data.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';

  return (
    <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="shared-portfolio-page">
      {/* Header */}
      <div className="rounded-lg border bg-card p-5 mb-4" data-testid="shared-portfolio-header">
        <div className="flex items-center gap-2 mb-2">
          <Share2 className="w-5 h-5 text-violet-500" />
          <span className="text-[10px] font-bold uppercase tracking-wider text-violet-600 dark:text-violet-400">Public Portfolio Snapshot</span>
          {/* Round 95x — Live-data freshness chip. Tells viewers the
              data isn't a stale screenshot — the snapshot auto-refreshes
              every time the owner opens their Share modal, so a recent
              timestamp = recent owner activity. Full ISO on hover for
              the analytical viewer. */}
          {data.created_at && (
            <button
              type="button"
              onClick={handleRefresh}
              disabled={refreshing}
              className="ml-auto inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-300/60 dark:border-emerald-800/60 hover:bg-emerald-200 dark:hover:bg-emerald-950/60 disabled:opacity-60 disabled:cursor-wait transition-colors"
              title={`Snapshot refreshed at ${new Date(data.created_at).toLocaleString()} — click to refresh all prices, yields, and totals now.`}
              data-testid="shared-portfolio-freshness-chip"
            >
              <span className={`w-1.5 h-1.5 rounded-full bg-emerald-500 ${refreshing ? 'animate-ping' : 'animate-pulse'}`} />
              {refreshing ? 'Refreshing…' : `Refreshed ${relTime(data.created_at)} · click to refresh`}
            </button>
          )}
        </div>
        <h1 className="text-2xl font-bold mb-1">{data.owner_name}'s Dividend Portfolio</h1>
        <p className="text-xs text-muted-foreground">
          Tracked on DiviDrip · Snapshot generated {created} · {data.position_count} position{data.position_count === 1 ? '' : 's'}
        </p>
        {/* Round 95x — Portfolio composition badges. Derived from the
            per-position flags already exposed in the positions table
            (is_adr, is_bdc, is_leveraged, uses_futures), aggregated
            here so viewers can read the strategy archetype at a
            glance without scrolling. Only shown when at least one
            position carries the flag, so a vanilla blue-chip
            portfolio doesn't get a row of empty chips. */}
        {(() => {
          const flagCounts = (data.positions || []).reduce((acc, p) => {
            if (p.is_adr) acc.adr += 1;
            if (p.is_bdc) acc.bdc += 1;
            if (p.is_leveraged) acc.lev += 1;
            if (p.uses_futures) acc.fut += 1;
            return acc;
          }, { adr: 0, bdc: 0, lev: 0, fut: 0 });
          const chips = [
            flagCounts.adr > 0 && { key: 'adr', label: 'ADRs', count: flagCounts.adr, tone: 'bg-pink-100 dark:bg-pink-950/40 text-pink-700 dark:text-pink-300 border-pink-300/60 dark:border-pink-800/60', icon: '🌍' },
            flagCounts.bdc > 0 && { key: 'bdc', label: 'BDCs', count: flagCounts.bdc, tone: 'bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border-amber-300/60 dark:border-amber-800/60', icon: '🏦' },
            flagCounts.lev > 0 && { key: 'lev', label: 'Leveraged', count: flagCounts.lev, tone: 'bg-orange-100 dark:bg-orange-950/40 text-orange-700 dark:text-orange-300 border-orange-300/60 dark:border-orange-800/60', icon: '⚡' },
            flagCounts.fut > 0 && { key: 'fut', label: 'Futures-Based', count: flagCounts.fut, tone: 'bg-purple-100 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300 border-purple-300/60 dark:border-purple-800/60', icon: '📈' },
          ].filter(Boolean);
          if (chips.length === 0) return null;
          return (
            <div className="flex items-center gap-1.5 mt-2 flex-wrap" data-testid="shared-portfolio-composition-chips">
              <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mr-1">Includes:</span>
              {chips.map(c => (
                <span
                  key={c.key}
                  className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${c.tone}`}
                  title={`${c.count} ${c.label} ${c.count === 1 ? 'position' : 'positions'} in this portfolio`}
                  data-testid={`shared-portfolio-chip-${c.key}`}
                >
                  <span aria-hidden>{c.icon}</span>
                  {c.label} ({c.count})
                </span>
              ))}
            </div>
          );
        })()}
      </div>

      {/* Stats grid */}
      {showDollars ? (
        <div className={`grid grid-cols-2 ${data.show_capital_gain && data.capital_gain != null ? 'sm:grid-cols-5' : data.show_yield_on_cost && data.yield_on_cost != null ? 'sm:grid-cols-5' : 'sm:grid-cols-4'} gap-3 mb-4`}>
          <StatCard
            label="Total Value"
            value={`$${Number(data.total_value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            sub="cash + positions"
            testid="shared-total-value"
            tip="Stocks at current market price PLUS uninvested cash. After a sale this stays roughly the same — stock value goes down, cash goes up by the proceeds."
          />
          <StatCard
            label="Stock Value"
            value={`$${Number(data.total_stock_value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            sub="positions only"
            testid="shared-stock-value"
            tip="Just the current market value of remaining stock positions. Drops after a sell because the shares are gone."
          />
          <StatCard
            label="Annual Dividends"
            value={`$${Number(data.annual_dividends || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            sub={`$${Number(data.monthly_dividends || 0).toFixed(2)}/mo`}
            color="text-green-700 dark:text-green-400"
            testid="shared-annual-divs"
            tip="Forward annual dividend run-rate based on each holding's current dividend rate × shares held."
          />
          <StatCard
            label="Portfolio Yield"
            value={`${Number(data.portfolio_yield || 0).toFixed(2)}%`}
            sub="on invested"
            color="text-blue-700 dark:text-blue-400"
            testid="shared-yield"
            tip="Annual dividends ÷ current stock value. Today's snapshot — moves with share price."
          />
          {data.show_yield_on_cost && data.yield_on_cost != null && (
            <StatCard
              label="Yield on Cost"
              value={`${Number(data.yield_on_cost).toFixed(2)}%`}
              sub="on cost basis"
              color="text-violet-700 dark:text-violet-400"
              testid="shared-yoc"
              tip="Annual dividends ÷ what was originally paid for the shares. Climbs over time as companies raise their dividends."
            />
          )}
          {data.show_capital_gain && data.capital_gain != null && (
            <StatCard
              label="Net Capital Gain"
              value={`${data.capital_gain >= 0 ? '+' : '−'}$${Math.abs(Number(data.capital_gain || 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              sub={data.capital_gain_pct != null ? `${data.capital_gain_pct >= 0 ? '+' : ''}${Number(data.capital_gain_pct).toFixed(2)}% all-time` : 'unrealized · drops on sell'}
              color={Number(data.capital_gain || 0) >= 0 ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'}
              testid="shared-capital-gain"
              tip="Total Value − Net Contributions − Dividends Collected. After a sale, the realized portion moves into cash and stops counting here even though total wealth didn't change."
            />
          )}
        </div>
      ) : (
        <div className={`grid grid-cols-2 ${data.show_yield_on_cost && data.yield_on_cost != null ? 'sm:grid-cols-3' : ''} gap-3 mb-4`}>
          <StatCard label="Positions" value={String(data.position_count || 0)} sub="tracked" testid="shared-position-count" />
          <StatCard label="Portfolio Yield" value={`${Number(data.portfolio_yield || 0).toFixed(2)}%`} sub="weighted average" color="text-blue-700 dark:text-blue-400" testid="shared-yield" tip="Annual dividends ÷ current stock value (weighted across all holdings)." />
          {data.show_yield_on_cost && data.yield_on_cost != null && (
            <StatCard
              label="Yield on Cost"
              value={`${Number(data.yield_on_cost).toFixed(2)}%`}
              sub="on cost basis"
              color="text-violet-700 dark:text-violet-400"
              testid="shared-yoc"
              tip="Annual dividends ÷ original cost basis. Climbs over time as dividends grow."
            />
          )}
        </div>
      )}

      {/* Sector breakdown */}
      {data.sectors?.length > 0 && (
        <div className="rounded-lg border bg-card p-5 mb-4" data-testid="shared-sector-breakdown">
          <h2 className="text-base font-bold mb-3">Sector Allocation</h2>
          <div className="space-y-2">
            {data.sectors.map((s, i) => (
              <div key={s.sector} className="flex items-center gap-3" data-testid={`shared-sector-row-${s.sector}`}>
                <span className="text-xs font-semibold w-32 truncate shrink-0">{s.sector}</span>
                <div className="flex-1 h-4 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full transition-all duration-500"
                    style={{ width: `${s.pct}%`, background: SECTOR_COLORS[i % SECTOR_COLORS.length] }}
                  />
                </div>
                <span className="text-xs font-mono font-bold w-12 text-right shrink-0">{s.pct.toFixed(1)}%</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Account-Type allocation — visibility is controlled solely by
          the owner's "Hide account-type allocation" toggle on the My
          Share Page modal. When that toggle is OFF, the backend returns
          a non-empty `accounts` array (even if it's a single "100%
          Taxable" row for users who haven't categorised lots yet);
          when ON, the array is empty and this section auto-hides.
          Pcts are by stock value, not lot count, so a one-share Roth
          lot doesn't drown out a hundred-share Taxable lot. */}
      {(data.accounts?.length || 0) >= 1 && (
        <div className="rounded-lg border bg-card p-5 mb-4" data-testid="shared-account-breakdown">
          <h2 className="text-base font-bold mb-1">Account-Type Allocation</h2>
          <p className="text-[11px] text-muted-foreground mb-3">
            Where the dollars live for tax purposes. Per-ticker placement is private.
          </p>
          <div className="flex w-full h-3 rounded-full overflow-hidden mb-3" data-testid="shared-account-bar">
            {data.accounts.map(a => {
              const tone = (ACCOUNT_TYPE_TONE[a.account_type] || '').split(' ')[0];
              return (
                <div
                  key={a.account_type}
                  className={tone || 'bg-slate-300'}
                  style={{ width: `${a.pct}%` }}
                  title={`${getAccountType(a.account_type).label}: ${a.pct}%`}
                />
              );
            })}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {data.accounts.map(a => {
              const at = getAccountType(a.account_type);
              const Icon = at.icon;
              return (
                <div
                  key={a.account_type}
                  className="flex items-center gap-2 rounded-md border border-border bg-muted/20 px-2 py-1.5"
                  data-testid={`shared-account-${a.account_type}`}
                >
                  <Icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground truncate">{at.short}</div>
                    <div className="font-mono font-bold text-sm">{a.pct.toFixed(1)}%</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Positions table */}
      <div className="rounded-lg border bg-card overflow-hidden" data-testid="shared-positions-table">
        <div className="px-5 py-3 border-b">
          <h2 className="text-base font-bold">Positions</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/30 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Ticker</th>
                <th className="text-left px-4 py-2 font-semibold hidden sm:table-cell">Name</th>
                {showDollars && <th className="text-right px-4 py-2 font-semibold">Price</th>}
                <th className="text-right px-4 py-2 font-semibold">Yield</th>
                {showDollars && <th className="text-right px-4 py-2 font-semibold hidden md:table-cell">Shares</th>}
                {showDollars && <th className="text-right px-4 py-2 font-semibold hidden sm:table-cell">Value</th>}
                <th className="text-right px-4 py-2 font-semibold">% of Stocks</th>
              </tr>
            </thead>
            <tbody>
              {(data.positions || []).map(p => (
                <tr key={p.ticker} className="border-b last:border-b-0 hover:bg-muted/20 transition-colors" data-testid={`shared-position-${p.ticker}`}>
                  <td className="px-4 py-2 font-mono font-bold text-yellow-600 dark:text-yellow-400">{p.ticker}</td>
                  <td className="px-4 py-2 text-xs text-muted-foreground hidden sm:table-cell truncate max-w-[200px]">{p.name}</td>
                  {showDollars && (
                    <td className="px-4 py-2 text-right font-mono">
                      {p.price != null ? `$${Number(p.price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                    </td>
                  )}
                  <td className="px-4 py-2 text-right font-mono font-semibold text-green-700 dark:text-green-400">{p.yield?.toFixed(2)}%</td>
                  {showDollars && <td className="px-4 py-2 text-right font-mono hidden md:table-cell">{Number(p.shares || 0).toFixed(2)}</td>}
                  {showDollars && <td className="px-4 py-2 text-right font-mono hidden sm:table-cell">${Number(p.value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>}
                  <td className="px-4 py-2 text-right font-mono font-semibold">{(p.value_pct || 0).toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* CTA */}
      <div className="mt-6 rounded-lg border border-violet-300 dark:border-violet-800 bg-gradient-to-br from-violet-50 to-blue-50 dark:from-violet-950/40 dark:to-blue-950/40 px-5 py-3 text-center" data-testid="shared-portfolio-cta">
        <div className="flex items-center justify-center gap-2 mb-1">
          <DollarSign className="w-5 h-5 text-violet-600 dark:text-violet-400" />
          <h3 className="text-lg font-bold">Build your own dividend portfolio</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          DiviDrip is free — track 5,350+ dividend stocks &amp; ETFs with a full dividend calendar, screener, and DRIP calculator.
        </p>
        {/* Round 92o++++++++M — Open DiviDrip + Share on X side-by-
            side. The X button is owner-visible content but rendered
            on every viewer (so a friend looking at the link can also
            re-share). Tweet copy adapts to the privacy toggle: dollar
            amounts are excluded when `show_dollar_amounts === false`
            so the share text never leaks numbers the owner chose to
            hide on-page. */}
        <div className="flex items-center justify-center gap-2 flex-wrap">
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold bg-violet-600 text-white hover:bg-violet-700 transition-colors"
            data-testid="shared-portfolio-cta-btn"
          >
            <TrendingUp className="w-4 h-4" />
            Open DiviDrip
          </Link>
          <a
            href={buildXShareUrl({
              owner: data.owner_name,
              positionCount: data.position_count || 0,
              yieldPct: Number(data.portfolio_yield || 0),
              yieldOnCost: data.show_yield_on_cost && data.yield_on_cost != null ? Number(data.yield_on_cost) : null,
              capitalGain: data.show_capital_gain && data.capital_gain != null ? Number(data.capital_gain) : null,
              capitalGainPct: data.show_capital_gain && data.capital_gain_pct != null ? Number(data.capital_gain_pct) : null,
              monthlyDividends: Number(data.monthly_dividends || 0),
              annualDividends: Number(data.annual_dividends || 0),
              showDollars,
              // Round 95x — Use the backend OG-rich wrapper as the tweet URL
              // so X / Discord / Slack unfurl with a per-portfolio rich card
              // (owner name, yield, monthly income) instead of the generic
              // site-level OG. Real browsers transparently meta-refresh
              // straight to /p/{slug} after the unfurler caches the tags.
              shareUrl: (() => {
                if (typeof window === 'undefined') return '';
                // Rewrite /p/{slug} → /api/share/og/{slug}. Keeps origin
                // consistent across preview vs production deployments.
                const m = window.location.pathname.match(/^\/p\/([^/]+)$/);
                return m
                  ? `${window.location.origin}/api/share/og/${m[1]}`
                  : window.location.href;
              })(),
            })}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold bg-black text-white hover:bg-zinc-800 dark:bg-white dark:text-black dark:hover:bg-zinc-200 transition-colors"
            data-testid="shared-portfolio-x-btn"
            title="Share this snapshot on X (formerly Twitter)"
          >
            <Twitter className="w-4 h-4" />
            Share on X
          </a>
        </div>
      </div>
    </div>
  );
}

// Round 95x — Compact relative-time formatter for the "Refreshed Xm
// ago" freshness chip. Short, opinionated thresholds: seconds become
// "just now" (looks cleaner than "0m ago"), then minutes/hours/days.
// Falls back to a localized date string past 30 days so a year-old
// chip doesn't read as a misleading "400d ago".
function relTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (diffSec < 45) return 'just now';
  if (diffSec < 90) return '1m ago';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function StatCard({ label, value, sub, color, testid, tip }) {
  return (
    <div
      className="rounded-lg border bg-card p-4 text-center"
      data-testid={testid}
      title={tip || undefined}
    >
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <p className={`text-xl font-bold font-mono ${color || ''}`}>{value}</p>
      {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
    </div>
  );
}

// Round 92o++++++++M / Round 95x — Build the X.com web-intent URL
// with adaptive copy. Now four variants ordered by what's most
// impressive given the owner's privacy settings:
//
//   With dollars + YoC visible (the "flex" tweet):
//     "{name}'s dividend portfolio: $X/mo at Y.YY% yield-on-cost 💧 (+$Z all-time)
//      Tracked live on DiviDrip — full dividend calendar & DRIP calc."
//
//   With dollars + capital gain visible (no YoC):
//     "{name}'s dividend portfolio is generating $X/mo at Y.YY% yield 💧 (+$Z all-time)"
//
//   With dollars visible, no opt-ins:
//     "{name}'s dividend portfolio is generating $X/mo at Y.YY% yield 💧"
//
//   With dollars hidden (privacy mode) — YoC can still appear since it's a ratio:
//     "{name}'s dividend portfolio — N positions at Y.YY% yield-on-cost 💧"
//     or fall back to plain weighted yield if YoC also hidden.
//
// X auto-shortens URLs to a t.co (~23 chars) and counts hashtags
// normally, so every variant stays comfortably under the 280-char
// limit. Hashtags chosen for reach in the FinTwit / dividend
// community without leaning into spammy "#stocks #money" noise.
function buildXShareUrl({ owner, positionCount, yieldPct, yieldOnCost, capitalGain, capitalGainPct, monthlyDividends, annualDividends, showDollars, shareUrl }) {
  const safeOwner = (owner || 'My').trim();
  const yieldStr = (yieldPct || 0).toFixed(2);
  const yocStr = (yieldOnCost != null && yieldOnCost > 0) ? yieldOnCost.toFixed(2) : null;
  // Lead with YoC when shown (it's the "I bought this years ago" flex);
  // otherwise lead with current portfolio yield.
  const leadYieldStr = yocStr || yieldStr;
  const leadYieldLabel = yocStr ? 'yield-on-cost' : 'yield';

  let lead;
  if (showDollars && monthlyDividends > 0) {
    lead = `${safeOwner}'s dividend portfolio is generating $${monthlyDividends.toFixed(2)}/mo at ${leadYieldStr}% ${leadYieldLabel} 💧`;
  } else if (showDollars && annualDividends > 0) {
    // Fallback when monthly isn't useful (small portfolios). Use annual.
    lead = `${safeOwner}'s dividend portfolio is generating $${annualDividends.toFixed(0)}/yr at ${leadYieldStr}% ${leadYieldLabel} 💧`;
  } else {
    // Privacy mode — no dollar amounts. YoC can still ride if the
    // owner opted in (it's a ratio).
    lead = `${safeOwner}'s dividend portfolio — ${positionCount} position${positionCount === 1 ? '' : 's'} at ${leadYieldStr}% ${yocStr ? 'yield-on-cost' : 'weighted yield'} 💧`;
  }

  // Capital-gain brag line — only when dollars + show_capital_gain
  // are both ON. Sign-aware so a portfolio that's down doesn't get
  // dressed up as a win.
  let gainLine = '';
  if (showDollars && capitalGain != null) {
    const sign = capitalGain >= 0 ? '+' : '−';
    const pctSuffix = capitalGainPct != null
      ? ` (${capitalGainPct >= 0 ? '+' : ''}${capitalGainPct.toFixed(2)}% all-time)`
      : ' all-time';
    gainLine = ` ${sign}$${Math.abs(capitalGain).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}${pctSuffix}`;
  }

  const body = `${lead}${gainLine}\n\nTracked live on DiviDrip — full dividend calendar & DRIP calculator.\n\n${shareUrl}\n\n#DividendInvesting #PassiveIncome`;
  return `https://twitter.com/intent/tweet?text=${encodeURIComponent(body)}`;
}
